import React, { useMemo } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import CurrencyField from "components/CurrencyField";

const getLookupList = (slmLookups, lookupKey) => {
  if (!Array.isArray(slmLookups)) {
    return [];
  }

  const lookupGroup = slmLookups.find((item) => item?.look_up === lookupKey);
  return Array.isArray(lookupGroup?.list) ? lookupGroup.list : [];
};

const mapLookupList = (list, candidateValueKeys, candidateLabelKeys) => {
  const firstDefinedValue = (option, keys) => {
    for (const key of keys) {
      if (option && Object.prototype.hasOwnProperty.call(option, key) && option[key] !== undefined && option[key] !== null) {
        return option[key];
      }
    }

    return undefined;
  };

  return list
    .map((option) => {
      if (typeof option === "string") {
        return { value: option, label: option };
      }

      const valueFromCandidates = firstDefinedValue(option, candidateValueKeys);
      const labelFromCandidates = firstDefinedValue(option, candidateLabelKeys);

      const value = valueFromCandidates ?? "";
      // Never fall back to the id: the API returns { value, label } and a label of "" is authoritative.
      const label = String(labelFromCandidates ?? "");

      return {
        ...option,
        value,
        label,
      };
    })
    .filter((option) => option.value !== undefined && option.value !== null);
};

export default function SLMLeaseInformationPanel({
  slmLookups,
  state,
  createId,
  currencyFormatter,
  reviewBasisOptions,
  rentPerAnnumBasisOptions,
  rentBasisOptions,
  outgoingsBasisOptions,
  onDateChange,
  onChange,
  onBlur,
  onNumberChange,
  onCheckBoxChange,
  onReviewBasisCategoryChange,
  onRentPerAnnumBasisCategoryChange,
  onRentBasisCategoryChange,
  onOutgoingsCategoryChange,
  onMoneyChange,
}) {
  const derivedReviewBasisOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupList(slmLookups, "lease_review_basis_category"),
      ["value", "lease_basis", "lease_basis_cat_id", "lease_review_basis_cat_id", "id", "code", "name", "label"],
      ["label", "lease_basis_cat", "lease_review_basis_category", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : reviewBasisOptions || [];
  }, [slmLookups, reviewBasisOptions]);

  const derivedRentPerAnnumBasisOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupList(slmLookups, "rent_per_annum_basis_category"),
      ["value", "rent_per_annum_basis_cat_id", "id", "code", "name", "label"],
      ["label", "rent_per_annum_basis_cat", "rent_per_annum_basis_category", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : rentPerAnnumBasisOptions || [];
  }, [slmLookups, rentPerAnnumBasisOptions]);

  const derivedRentBasisOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupList(slmLookups, "rent_basis_category"),
      ["value", "rent_basis", "rent_basis_cat_id", "id", "code", "name", "label"],
      ["label", "rent_basis_cat", "rent_basis_category", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : rentBasisOptions || [];
  }, [slmLookups, rentBasisOptions]);

  const derivedOutgoingsBasisOptions = useMemo(() => {
    const lookupValues = mapLookupList(
      getLookupList(slmLookups, "lease_outgoings_basis_category"),
      ["value", "outgoing_basis_cat_id", "lease_outgoings_basis_cat_id", "id", "code", "name", "label"],
      ["label", "outgoing_basis_cat", "lease_outgoings_basis_category", "name", "description", "code", "value"]
    );

    return lookupValues.length > 0 ? lookupValues : outgoingsBasisOptions || [];
  }, [slmLookups, outgoingsBasisOptions]);

  return (
    <Card className="mb-2">
      <Card.Header>Lease Information</Card.Header>

      <Card.Body>
        <Row className="g-2">
          <Col>
            <Form.Group as={Row} controlId="stat_date">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Stats Date
              </Form.Label>
              <Col>
                <DatePicker id={createId("field", "stat-date", "input")} selected={state.stat_date !== null ? new Date(state.stat_date) : null} onChange={(e) => onDateChange(e, "stat_date")} onBlur={onBlur} dateFormat="dd/MM/yyyy" className="form-control" isClearable={true} />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="lease_commence_date">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Commencement Date
              </Form.Label>
              <Col>
                <DatePicker id={createId("field", "commence-date", "input")} selected={state.lease_commence_date !== null ? new Date(state.lease_commence_date) : null} onChange={(e) => onDateChange(e, "lease_commence_date")} onBlur={onBlur} dateFormat="dd/MM/yyyy" className="form-control" isClearable={true} />
              </Col>
            </Form.Group>

            <Form.Group as={Row} controlId="term">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Lease Term
              </Form.Label>
              <Col>
                <Form.Control id={createId("field", "lease-term", "input")} name="term" onChange={onNumberChange} value={state.term ?? ""} as="input" type="text" onBlur={onBlur} />
              </Col>
              <Col xs="auto" className="d-flex align-items-center px-1 text-muted small">years</Col>
            </Form.Group>

            <Form.Group as={Row} controlId="options">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Options Text
              </Form.Label>
              <Col>
                <Form.Control id={createId("field", "options-text", "input")} name="options" onChange={onChange} value={state.options ?? ""} as="input" type="text" maxLength={5} onBlur={onBlur} />
              </Col>
              <Col xs="auto" className="d-flex align-items-center px-1 text-muted small">x + x format</Col>
            </Form.Group>
            <Form.Group as={Row} controlId="review">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Reviews every
              </Form.Label>
              <Col>
                <Form.Control id={createId("field", "reviews-every", "input")} name="review" onChange={onChange} value={state.review ?? ""} as="input" type="text" onBlur={onBlur} />
              </Col>
              <Col xs="auto" className="d-flex align-items-center px-1 text-muted small">years</Col>
            </Form.Group>

            <Form.Group as={Row} controlId="review_basis">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Lease Review Basis
              </Form.Label>
              <Col>
                <Select value={state.selectedReviewBasisCategories} onChange={onReviewBasisCategoryChange} options={derivedReviewBasisOptions} inputId={createId("field", "lease-review-basis", "input")} instanceId={createId("field", "lease-review-basis", "instance")} isClearable />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="rent_per_annum">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Rent per Annum
              </Form.Label>
              <Col>
                <Form.Control id={createId("field", "rent-per-annum", "input")} name="rent_per_annum" onChange={onNumberChange} value={state.rent_per_annum ? currencyFormatter.format(state.rent_per_annum) : (state.rent_per_annum ?? "")} as="input" type="text" onBlur={onBlur} />
              </Col>
              <Col style={{ verticalAlign: "top" }}>
                <Select value={state.selectedRentPerAnnumBasisCategories} onChange={onRentPerAnnumBasisCategoryChange} options={derivedRentPerAnnumBasisOptions} inputId={createId("field", "rent-per-annum-basis", "input")} instanceId={createId("field", "rent-per-annum-basis", "instance")} isClearable />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="rent">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Rent per SQM
              </Form.Label>
              <Col>
                <Form.Control id={createId("field", "rent-per-sqm", "input")} name="rent" onChange={onNumberChange} value={state.rent ? currencyFormatter.format(state.rent) : (state.rent ?? "")} as="input" type="text" onBlur={onBlur} />
              </Col>
              <Col style={{ verticalAlign: "top" }}>
                <Select value={state.selectedRentBasisCategories} onChange={onRentBasisCategoryChange} options={derivedRentBasisOptions} inputId={createId("field", "rent-basis", "input")} instanceId={createId("field", "rent-basis", "instance")} isClearable />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="estimated">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Rental Estimated?
              </Form.Label>
              <Col>
                <Form.Check id={createId("field", "rental-estimated", "checkbox")} checked={state.estimated} onChange={onCheckBoxChange} name="estimated" type="checkbox" />
              </Col>
            </Form.Group>

            <Form.Group as={Row} controlId="car_rate">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Car Rate pcm
              </Form.Label>
              <Col>
                <Form.Control id={createId("field", "car-rate-pcm", "input")} name="car_rate" onChange={onNumberChange} value={state.car_rate ? currencyFormatter.format(state.car_rate) : (state.car_rate ?? "")} as="input" type="text" onBlur={onBlur} />
              </Col>
              <Col xs="auto" className="d-flex align-items-center px-1 text-muted small">per calendar month</Col>
            </Form.Group>

            <Form.Group as={Row} controlId="outgoings">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Outgoings per SQM
              </Form.Label>
              <Col>
                <CurrencyField id={createId("field", "outgoings-per-sqm", "input")} name="outgoings" value={state.outgoings} onChange={onMoneyChange} onBlur={onBlur} />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="outgoings_cat">
              <Form.Label as={Col} className="text-right" column sm={6} md={6} lg={4}>
                Outgoings Basis
              </Form.Label>

              <Col style={{ verticalAlign: "top" }}>
                <Select value={state.selectedOutgoingsCategories} onChange={onOutgoingsCategoryChange} options={derivedOutgoingsBasisOptions} inputId={createId("field", "lease-outgoings-basis", "input")} instanceId={createId("field", "lease-outgoings-basis", "instance")} isClearable />
              </Col>
            </Form.Group>

            <Form.Group as={Row} controlId="editors_note">
              <Form.Label>
                <b>Editors Notes</b>
                <br />
                (Unpublished)
              </Form.Label>

              <Form.Control id={createId("field", "editors-note", "input")} name="editors_note" onChange={onChange} value={state.editors_note ?? ""} as="textarea" type="text" rows="5" onBlur={onBlur} bsPrefix="internal" />
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}
